'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface Option {
  id: string;
  name: string;
}

interface Criterion {
  id: string;
  name: string;
  weight: number;
  description?: string;
}

interface Score {
  optionId: string;
  criterionId: string;
  value: number;
  commentary?: string;
}

export default function ArchitectureCalculatorPage() {
  const [options, setOptions] = useState<Option[]>([
    { id: '1', name: 'Option A' },
    { id: '2', name: 'Option B' },
  ]);
  const [criteria, setCriteria] = useState<Criterion[]>([
    { id: '1', name: 'Scalability', weight: 5, description: 'Ability to scale with growth' },
    { id: '2', name: 'Development Speed', weight: 4, description: 'Time to market and iteration speed' },
    { id: '3', name: 'Team Experience', weight: 3, description: 'Team familiarity with the approach' },
  ]);
  const [scores, setScores] = useState<Score[]>([]);
  const [newOption, setNewOption] = useState('');
  const [newCriterion, setNewCriterion] = useState({ name: '', weight: 3, description: '' });
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'architecture-calculator',
    toolCategory: 'calculator',
  });

  // Calculate weighted scores for each option
  const calculateScores = () => {
    return options.map(option => {
      const optionScores = criteria.map(criterion => {
        const score = scores.find(s => s.optionId === option.id && s.criterionId === criterion.id);
        return {
          criterion: criterion.name,
          weight: criterion.weight,
          score: score?.value || 0,
          weighted: (score?.value || 0) * criterion.weight,
          commentary: score?.commentary || '',
        };
      });

      const totalWeighted = optionScores.reduce((sum, s) => sum + s.weighted, 0);
      const maxPossible = criteria.reduce((sum, c) => sum + (c.weight * 5), 0);
      const percentage = maxPossible > 0 ? (totalWeighted / maxPossible) * 100 : 0;

      return {
        option,
        scores: optionScores,
        total: totalWeighted,
        percentage,
      };
    }).sort((a, b) => b.total - a.total);
  };

  const results = calculateScores();

  const addOption = () => {
    if (newOption.trim()) {
      setOptions([...options, { id: Date.now().toString(), name: newOption.trim() }]);
      setNewOption('');
    }
  };

  const addCriterion = () => {
    if (newCriterion.name.trim()) {
      setCriteria([...criteria, {
        id: Date.now().toString(),
        name: newCriterion.name.trim(),
        weight: newCriterion.weight,
        description: newCriterion.description.trim(),
      }]);
      setNewCriterion({ name: '', weight: 3, description: '' });
    }
  };

  const removeOption = (id: string) => {
    setOptions(options.filter(o => o.id !== id));
    setScores(scores.filter(s => s.optionId !== id));
  };

  const removeCriterion = (id: string) => {
    setCriteria(criteria.filter(c => c.id !== id));
    setScores(scores.filter(s => s.criterionId !== id));
  };

  const updateScore = (optionId: string, criterionId: string, value: number) => {
    const existing = scores.find(s => s.optionId === optionId && s.criterionId === criterionId);
    if (existing) {
      setScores(scores.map(s =>
        s.optionId === optionId && s.criterionId === criterionId
          ? { ...s, value }
          : s
      ));
    } else {
      setScores([...scores, { optionId, criterionId, value }]);
    }
  };

  const updateCommentary = (optionId: string, criterionId: string, commentary: string) => {
    const existing = scores.find(s => s.optionId === optionId && s.criterionId === criterionId);
    if (existing) {
      setScores(scores.map(s =>
        s.optionId === optionId && s.criterionId === criterionId
          ? { ...s, commentary }
          : s
      ));
    } else {
      setScores([...scores, { optionId, criterionId, value: 0, commentary }]);
    }
  };

  const getScore = (optionId: string, criterionId: string): number => {
    return scores.find(s => s.optionId === optionId && s.criterionId === criterionId)?.value || 0;
  };

  const getCommentary = (optionId: string, criterionId: string): string => {
    return scores.find(s => s.optionId === optionId && s.criterionId === criterionId)?.commentary || '';
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled || results.length === 0) return;

    const winner = results[0];
    const optionScores: Record<string, number> = {};
    results.forEach(r => {
      optionScores[r.option.name] = r.percentage;
    });

    const recommendations = results
      .filter((_, idx) => idx > 0)
      .map((r, idx) => ({
        id: r.option.id,
        title: r.option.name,
        priority: idx === 0 ? 'medium' as const : 'low' as const,
        description: `Scored ${r.percentage.toFixed(0)}% (${r.total.toFixed(1)} points)`,
      }));

    const assessment = await saveResult({
      name: `Architecture Decision - ${new Date().toLocaleDateString()}`,
      score_overall: winner.percentage,
      scores: optionScores,
      answers: {
        options: options.map(o => o.name),
        criteria: criteria.map(c => ({ name: c.name, weight: c.weight })),
        scores: scores,
      },
      recommendations,
      metadata: {
        winningOption: winner.option.name,
        winningScore: winner.total,
        winningPercentage: winner.percentage,
        totalOptions: options.length,
        totalCriteria: criteria.length,
        criteriaBreakdown: winner.scores.map(s => ({
          criterion: s.criterion,
          weight: s.weight,
          score: s.score,
          weighted: s.weighted,
        })),
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">🏗️</span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">
              Architecture Decision Calculator
            </h1>
          </div>
          <p className="text-xl text-foreground-secondary">
            Score and compare architectural approaches based on your specific requirements
          </p>
        </div>

        {/* Breadcrumb */}
        <nav className="mb-8 text-sm text-foreground-tertiary">
          <Link href="/" className="hover:text-accent transition-colors">
            Home
          </Link>
          <span className="mx-2">/</span>
          <Link href="/tools" className="hover:text-accent transition-colors">
            Tools
          </Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Architecture Calculator</span>
        </nav>

        {/* Instructions */}
        <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
          <h3 className="font-bold text-accent mb-2">How to use this calculator</h3>
          <ol className="text-foreground-secondary text-sm space-y-2 list-decimal list-inside">
            <li>Add or edit the options you&apos;re comparing (e.g., Microservices vs Monolith)</li>
            <li>Add or edit criteria that matter for your decision with weights (1-5)</li>
            <li>Score each option (1-5) for each criterion and add rationale to explain your reasoning</li>
            <li>Review the weighted results and score breakdowns to see which option is best</li>
          </ol>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column: Options & Criteria Setup */}
          <div className="lg:col-span-1 space-y-6">
            {/* Options Management */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Options</h3>
              <div className="space-y-2 mb-4">
                {options.map(option => (
                  <div key={option.id} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={option.name}
                      onChange={(e) => setOptions(options.map(o =>
                        o.id === option.id ? { ...o, name: e.target.value } : o
                      ))}
                      className="flex-1 px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                    />
                    <button
                      onClick={() => removeOption(option.id)}
                      className="px-3 py-2 text-accent hover:text-accent transition-colors"
                      title="Remove option"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="New option..."
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addOption()}
                  className="flex-1 px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                />
                <button
                  onClick={addOption}
                  className="px-4 py-2 bg-accent text-accent-foreground rounded font-semibold hover:bg-accent-hover transition-colors text-sm"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Criteria Management */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Criteria</h3>
              <div className="space-y-3 mb-4">
                {criteria.map(criterion => (
                  <div key={criterion.id} className="border border-border-muted rounded p-3">
                    <div className="flex items-start gap-2 mb-2">
                      <input
                        type="text"
                        value={criterion.name}
                        onChange={(e) => setCriteria(criteria.map(c =>
                          c.id === criterion.id ? { ...c, name: e.target.value } : c
                        ))}
                        placeholder="Criterion name"
                        className="flex-1 px-2 py-1 bg-background border border-border rounded text-foreground text-sm"
                      />
                      <button
                        onClick={() => removeCriterion(criterion.id)}
                        className="px-2 py-1 text-accent hover:text-accent transition-colors"
                        title="Remove criterion"
                      >
                        ✕
                      </button>
                    </div>
                    <input
                      type="text"
                      value={criterion.description || ''}
                      onChange={(e) => setCriteria(criteria.map(c =>
                        c.id === criterion.id ? { ...c, description: e.target.value } : c
                      ))}
                      placeholder="Description (optional)"
                      className="w-full px-2 py-1 mb-2 bg-background border border-border rounded text-foreground text-xs"
                    />
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-foreground-tertiary">Weight:</label>
                      <input
                        type="range"
                        min="1"
                        max="5"
                        value={criterion.weight}
                        onChange={(e) => setCriteria(criteria.map(c =>
                          c.id === criterion.id ? { ...c, weight: parseInt(e.target.value) } : c
                        ))}
                        className="flex-1"
                      />
                      <span className="text-sm text-accent font-bold w-6 text-center">
                        {criterion.weight}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Criterion name..."
                  value={newCriterion.name}
                  onChange={(e) => setNewCriterion({ ...newCriterion, name: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                />
                <input
                  type="text"
                  placeholder="Description (optional)..."
                  value={newCriterion.description}
                  onChange={(e) => setNewCriterion({ ...newCriterion, description: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                />
                <div className="flex items-center gap-2">
                  <label className="text-xs text-foreground-tertiary">Weight:</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={newCriterion.weight}
                    onChange={(e) => setNewCriterion({ ...newCriterion, weight: parseInt(e.target.value) })}
                    className="flex-1"
                  />
                  <span className="text-sm text-accent font-bold w-6 text-center">
                    {newCriterion.weight}
                  </span>
                </div>
                <button
                  onClick={addCriterion}
                  className="w-full px-4 py-2 bg-accent text-accent-foreground rounded font-semibold hover:bg-accent-hover transition-colors text-sm"
                >
                  Add Criterion
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: Scoring Matrix & Results */}
          <div className="lg:col-span-2 space-y-6">
            {/* Scoring Matrix */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Score Each Option</h3>
              <p className="text-sm text-foreground-tertiary mb-4">
                Rate each option from 1 (poor) to 5 (excellent) for each criterion, and add commentary to explain your rationale
              </p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 text-foreground font-semibold">Criterion</th>
                      <th className="text-center py-3 px-2 text-foreground-tertiary text-sm">Weight</th>
                      {options.map(option => (
                        <th key={option.id} className="text-center py-3 px-2 text-foreground font-semibold">
                          {option.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {criteria.map(criterion => (
                      <tr key={criterion.id} className="border-b border-border-muted">
                        <td className="py-3 px-2 text-foreground-secondary">
                          <div className="font-medium">{criterion.name}</div>
                          {criterion.description && (
                            <div className="text-xs text-foreground-tertiary mt-1">{criterion.description}</div>
                          )}
                        </td>
                        <td className="text-center py-3 px-2">
                          <span className="inline-block px-2 py-1 bg-accent-muted text-accent rounded text-sm font-bold">
                            {criterion.weight}
                          </span>
                        </td>
                        {options.map(option => (
                          <td key={option.id} className="py-3 px-2">
                            <div className="flex flex-col gap-2">
                              <select
                                value={getScore(option.id, criterion.id)}
                                onChange={(e) => updateScore(option.id, criterion.id, parseInt(e.target.value))}
                                className="w-full px-2 py-1 bg-background border border-border rounded text-foreground text-sm"
                              >
                                <option value="0">-</option>
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                                <option value="4">4</option>
                                <option value="5">5</option>
                              </select>
                              <textarea
                                value={getCommentary(option.id, criterion.id)}
                                onChange={(e) => updateCommentary(option.id, criterion.id, e.target.value)}
                                placeholder="Rationale..."
                                className="w-full px-2 py-1 bg-background border border-border rounded text-foreground text-xs resize-none"
                                rows={2}
                              />
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Results */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Results</h3>
              <div className="space-y-4">
                {results.map((result, index) => (
                  <div
                    key={result.option.id}
                    className={`p-4 rounded-lg border ${
                      index === 0
                        ? 'border-accent bg-accent-muted'
                        : 'border-border bg-background/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {index === 0 && <span className="text-2xl">🏆</span>}
                        <h4 className="text-lg font-bold text-foreground">{result.option.name}</h4>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-accent">
                          {result.total.toFixed(1)}
                        </div>
                        <div className="text-sm text-foreground-tertiary">
                          {result.percentage.toFixed(0)}%
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-3">
                      <div className="h-2 bg-background rounded-full overflow-hidden">
                        <div
                          className="h-full bg-accent transition-all"
                          style={{ width: `${result.percentage}%` }}
                        />
                      </div>
                    </div>

                    {/* Score Breakdown */}
                    <details className="text-sm">
                      <summary className="cursor-pointer text-foreground-tertiary hover:text-foreground">
                        View score breakdown
                      </summary>
                      <div className="mt-2 space-y-2">
                        {result.scores.map((s, i) => (
                          <div key={i} className="border-l-2 border-border pl-3">
                            <div className="flex justify-between text-foreground-secondary">
                              <span>{s.criterion}:</span>
                              <span>
                                {s.score} × {s.weight} = <span className="text-accent">{s.weighted}</span>
                              </span>
                            </div>
                            {s.commentary && (
                              <div className="text-xs text-foreground-tertiary italic mt-1">
                                &quot;{s.commentary}&quot;
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                ))}
              </div>

              {results.length > 0 && results[0].total > 0 && (
                <div className="mt-6 p-4 bg-accent-muted rounded-lg border border-border">
                  <h4 className="font-bold text-accent mb-2">Recommendation</h4>
                  <p className="text-foreground-secondary text-sm">
                    Based on your weighted criteria, <strong className="text-foreground">{results[0].option.name}</strong> scores
                    highest with {results[0].total.toFixed(1)} points ({results[0].percentage.toFixed(0)}% of maximum possible score).
                    {results.length > 1 && results[1].total > 0 && (
                      <span>
                        {' '}It outperforms {results[1].option.name} by{' '}
                        {(results[0].total - results[1].total).toFixed(1)} points.
                      </span>
                    )}
                  </p>
                </div>
              )}

              {/* Command Center Save Button */}
              {results.length > 0 && results[0].total > 0 && (
                <div className="mt-4 flex flex-wrap gap-4">
                  {commandCenterEnabled && !saved && (
                    <button
                      onClick={handleSaveToCommandCenter}
                      disabled={saving}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Saving...' : 'Save to Command Center'}
                    </button>
                  )}
                  {saved && (
                    <div className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Saved to Command Center
                    </div>
                  )}
                  {saveError && (
                    <div className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
                      {saveError}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Example Templates */}
        <div className="mt-8 bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-bold text-foreground mb-3">Common Architecture Decisions</h3>
          <p className="text-sm text-foreground-tertiary mb-4">
            Try these common scenarios or create your own custom decision:
          </p>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="p-3 bg-background rounded border border-border-muted">
              <h4 className="font-semibold text-foreground text-sm mb-1">Microservices vs Monolith</h4>
              <p className="text-xs text-foreground-tertiary">Team size, scalability, complexity</p>
            </div>
            <div className="p-3 bg-background rounded border border-border-muted">
              <h4 className="font-semibold text-foreground text-sm mb-1">Database Selection</h4>
              <p className="text-xs text-foreground-tertiary">SQL vs NoSQL vs Graph databases</p>
            </div>
            <div className="p-3 bg-background rounded border border-border-muted">
              <h4 className="font-semibold text-foreground text-sm mb-1">Caching Strategy</h4>
              <p className="text-xs text-foreground-tertiary">Redis, Memcached, or CDN</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
